import { Box, Button, Stack, Typography, Alert, CircularProgress } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { MembershipSelectionComponent } from "../designer-application/membership-selection/membership-selection-component";
import { useDesignerApplicationContext } from "../../hooks/designer-application-context";
import { useEffect, useMemo, useState } from "react";
import { PaymentModal } from "../../components/payment-modal";
import { DesignerSubscriptionType, DesignerApplicationSteps } from "../../__generated__/types";
import { usePaymentSettings } from "../../contexts/PaymentSettingsContext";
import { useNotification } from "../../contexts/NotificationContext";
import { useUpdateMembershipApplicationMutation } from "../../hooks/useUpdateMembershipApplicationMutation";
import { useHighLevelContext } from "../../hooks/HighLevelContext";
import type { Plan } from "../../hooks/HighLevelContext/types";
import { DesignerAddOnOffer } from "../../components/designer-add-on-offer";
import { getPlanIdFromMembershipTier } from "../../utils/ecommerce-helpers";
import { useUserContext } from "../../hooks/userContext";
import {
  DESIGNER_SMART_GUILD_DUE_NOW_DEFAULT,
  getDesignerSmartGuildBillingSplit,
} from "../../utils/designer-smart-guild-pricing";

const toBillingCycle = (value?: string | null): string => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "bi-annually" || normalized === "biannually") {
    return "bi-annually";
  }
  if (normalized === "monthly") {
    return "monthly";
  }
  if (normalized === "annually") {
    return "annually";
  }
  return "annually";
};

export const DesignerMembershipSelectionPage = () => {
  const navigate = useNavigate();
  const { application, refreshApplicationData } = useDesignerApplicationContext();
  const { refreshUserData } = useUserContext();
  const { getPlanList } = useHighLevelContext();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const { settings, isPaymentEnabled, isTestMode } = usePaymentSettings();
  const { showNotification } = useNotification();
  const { updateApplication: updateApplicationMutation } = useUpdateMembershipApplicationMutation();

  const handleGoBack = () => {
    navigate("/dashboard");
  };

  useEffect(() => {
    let cancelled = false;

    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const list = await getPlanList();
        if (cancelled) {
          return;
        }
        setAvailablePlans(Array.isArray(list?.items) ? list.items : []);
      } catch (error) {
        if (!cancelled) {
          setAvailablePlans([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingPlans(false);
        }
      }
    };

    void loadPlans();

    return () => {
      cancelled = true;
    };
  }, [getPlanList]);

  const selectedPlanId = useMemo(() => {
    if (application?.membershipPlanId) {
      return application.membershipPlanId;
    }
    return getPlanIdFromMembershipTier(
      application?.membershipTier as DesignerSubscriptionType | null | undefined
    );
  }, [application?.membershipPlanId, application?.membershipTier]);

  const selectedPlan = useMemo(() => {
    if (!selectedPlanId && !application?.membershipTier) {
      return null;
    }
    const planById =
      selectedPlanId != null
        ? availablePlans.find((plan) => plan._id === selectedPlanId) ?? null
        : null;
    if (planById) {
      return planById;
    }

    const membershipTierToken = String(application?.membershipTier ?? "")
      .trim()
      .toLowerCase();

    if (membershipTierToken.includes("guild")) {
      return (
        availablePlans.find((plan) => getDesignerSmartGuildBillingSplit(plan) != null) ??
        null
      );
    }

    if (!membershipTierToken) {
      return null;
    }

    return (
      availablePlans.find((plan) => {
        const metadata =
          plan?.metadata && typeof plan.metadata === "object"
            ? (plan.metadata as Record<string, unknown>)
            : {};
        const lookup = `${String(plan?.name ?? "")} ${String(
          plan?.slug ?? ""
        )} ${String(metadata.tier ?? "")} ${String(
          metadata.subscriptionValue ?? ""
        )}`
          .toLowerCase()
          .replace(/[\s_]+/g, "");
        return lookup.includes(membershipTierToken.replace(/[\s_]+/g, ""));
      }) ?? null
    );
  }, [application?.membershipTier, availablePlans, selectedPlanId]);

  const selectedPlanSplit = useMemo(
    () => getDesignerSmartGuildBillingSplit(selectedPlan),
    [selectedPlan]
  );

  const hasGuildTierSelected = useMemo(
    () => String(application?.membershipTier ?? "").toLowerCase().includes("guild"),
    [application?.membershipTier]
  );

  const selectedPlanAmount = Number(
    selectedPlanSplit?.dueNowAmount ??
      selectedPlan?.metadata?.dueNowAmount ??
      selectedPlan?.pricing?.price ??
      (hasGuildTierSelected ? DESIGNER_SMART_GUILD_DUE_NOW_DEFAULT : 0)
  );
  const selectedPlanName =
    selectedPlan?.name ||
    (hasGuildTierSelected
      ? "Marketing Membership: Smart Guild"
      : null) ||
    String(application?.membershipTier || "Design");
  const selectedPlanBillingCycle = toBillingCycle(
    selectedPlan?.pricing?.period ?? selectedPlan?.metadata?.billingCycle
  );

  const handleProceedToPayment = async () => {
    if (!isMembershipSelected) {
      return;
    }

    if (isLoadingPlans) {
      showNotification({
        type: "info",
        message: "Loading membership pricing. Please wait a moment and try again.",
        duration: 4000,
      });
      return;
    }

    if (!isPaymentEnabled() || isTestMode()) {
      await simulateTestPayment();
      return;
    }

    if (selectedPlanAmount <= 0) {
      showNotification({
        type: "error",
        message: "Unable to load selected plan pricing. Please re-select your membership.",
        duration: 5000,
      });
      return;
    }

    setShowPaymentModal(true);
  };

  const simulateTestPayment = async () => {
    setIsProcessingPayment(true);
    
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update application status to MembershipActive
      if (application?._id) {
        await updateApplicationMutation({
          application: {
            _id: application._id,
            status: DesignerApplicationSteps.MembershipActive
          }
        });
        
        // Refresh application data from server to get updated status
        if (refreshApplicationData) {
          await refreshApplicationData();
        }
      }
      refreshUserData?.();
      
      // Show success notification
      showNotification({
        type: 'success',
        message: 'Payment processed successfully! Welcome to your new membership tier.',
        duration: 5000
      });
      
      // Navigate back to dashboard
      navigate("/dashboard");
      
    } catch (error) {
      showNotification({
        type: 'error',
        message: 'Payment processing failed. Please try again.',
        duration: 5000
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    
    try {
      // Update application status to MembershipActive
      if (application?._id) {
        await updateApplicationMutation({
          application: {
            _id: application._id,
            status: DesignerApplicationSteps.MembershipActive
          }
        });
        
        // Refresh application data from server to get updated status
        if (refreshApplicationData) {
          await refreshApplicationData();
        }
      }
      refreshUserData?.();
      
      showNotification({
        type: 'success',
        message: 'Payment processed successfully! Welcome to your new membership tier.',
        duration: 5000
      });
    } catch (error) {
      showNotification({
        type: "warning",
        message:
          "Payment was successful, but application status refresh is delayed. Please reload dashboard.",
        duration: 5000,
      });
    }
    
    navigate("/dashboard");
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
  };

  const handlePaymentFailure = (errorMessage: string) => {
    showNotification({
      type: "error",
      message: errorMessage || "Payment processing failed. Please try again.",
      duration: 5000,
    });
  };

  // Check if a membership is selected
  const isMembershipSelected = !!application?.membershipTier;

  return (
    <Box sx={{ pb: 10 }}> {/* Add bottom padding to account for fixed footer */}

      {/* Title */}
      <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>
        Choose Your Membership Level
      </Typography>

      {/* Test Mode Banner */}
      {(!isPaymentEnabled() || isTestMode()) && (
        <Alert 
          severity={isPaymentEnabled() ? "warning" : "info"}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            <strong>Beta Mode:</strong> {settings.testModeMessage}
          </Typography>
        </Alert>
      )}

      {/* Membership Selection */}
      <Box sx={{ mb: 4 }}>
        <MembershipSelectionComponent
          additionalCards={[
            <DesignerAddOnOffer
              useMembershipCardShell
              key="designer-addon-offer"
            />,
          ]}
        />
      </Box>

      {/* Fixed Footer */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, sm: '240px' }, // align with dashboard sidebar width
          right: 0,
          width: { xs: '100%', sm: 'calc(100% - 240px)' },
          backgroundColor: '#fff',
          borderTop: '1px solid #e5e7eb',
          zIndex: 10,
          px: 3, // Add padding for content
          py: 2,
          margin: 0, // No margin - flush with viewport bottom
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Button
            sx={{
              color: "#000",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 400,
              padding: "8px 0",
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#666",
              },
            }}
            startIcon={<ArrowBack />}
            onClick={handleGoBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleProceedToPayment}
            disabled={!isMembershipSelected || isProcessingPayment || isLoadingPlans}
            sx={{
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#333',
              },
              px: 4,
              py: 1.5,
            }}
          >
            {isProcessingPayment ? (
              <>
                <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                Processing...
              </>
            ) : (
              isPaymentEnabled() && !isTestMode() 
                ? "Proceed to Payment" 
                : "Complete Membership"
            )}
          </Button>
        </Stack>
      </Box>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={handlePaymentCancel}
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
          // Pass designer-specific data instead of homeowner data
          userType="designer"
          membershipTier={application?.membershipTier as DesignerSubscriptionType}
          selectedPlanId={application?.membershipPlanId}
          planData={selectedPlan ?? undefined}
          amount={selectedPlanAmount}
          planName={selectedPlanName}
          billingCycle={selectedPlanBillingCycle}
        />
      )}
    </Box>
  );
};
